import Button, { ButtonHTMLTag, ButtonSize, ButtonStyle } from '@/app/components/Button/Button';
import Dialog from '@/app/components/Dialog/Dialog';
import { DownloadIcon } from '@/app/components/DownloadIcon/DownloadIcon';
import Input from '@/app/components/Input/Input';
import { useToast } from '@/app/hooks/useToast';
import { projectsApiForClient } from '@/app/utils/apiForClient';
import { envs } from '@/app/utils/envs';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/nextjs';
import { CheckGreen } from '@/app/components/CheckGreen/CheckGreen';
import useAnalytics from '@/app/hooks/useAnalytics';
import { Project } from '@/app/api';
import { ShareMethod } from '@/app/hooks/useAnalytics/events/project-detail/shared-event';

// Assuming `blobResponse` is the Blob object received from the API
function downloadBlob(blob: Blob, fileName: string) {
  // Create a URL for the blob object
  const url = URL.createObjectURL(blob);

  // Create an anchor element
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName; // Set the desired file name

  // Append the anchor to the body, trigger click, then remove it
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);

  // Revoke the blob URL to free memory
  URL.revokeObjectURL(url);
}

type PrintPosterProps = {
  isOpen: boolean;
  onClose: () => void;
  defaultEmailValue: string;
  project: Project;
};

const regexEmail = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

export const PrintPosterDialog = ({ isOpen, onClose, defaultEmailValue, project }: PrintPosterProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState(defaultEmailValue);
  const [emailErrorMessage, setEmailErrorMessage] = useState('');
  const [isEmailSentSuccessfully, setIsEmailSentSuccessfully] = useState(false);
  const { showErrorToast } = useToast();
  const { t } = useTranslation();
  const {
    projectDetail: { sharedEvent },
  } = useAnalytics();

  useEffect(() => {
    setEmail(defaultEmailValue);
  }, [defaultEmailValue]);

  const handleSendPosterByEmail = async () => {
    if (!email) {
      return setEmailErrorMessage(t('validationErrors.required'));
    }

    const isEmailValid = regexEmail.test(email);

    if (!isEmailValid) {
      return setEmailErrorMessage(t('validationErrors.email'));
    }
    setIsLoading(true);

    try {
      await projectsApiForClient.postSendPosterByEmail({
        id: project.id,
        projectPosterSendEmailRequest: {
          email,
          shareSourceType: 'other',
        },
      });
      setIsEmailSentSuccessfully(true);
    } catch (error) {
      Sentry.captureException(error);
      console.error(error);
      setIsEmailSentSuccessfully(false);
      showErrorToast(t('components.PrintPosterDialog.sendError'));
    }
    sharedEvent(project, ShareMethod.PRINT);
    setIsLoading(false);
  };

  const handleDownload = async () => {
    sharedEvent(project, ShareMethod.PRINT);
    try {
      setIsLoading(true);
      const blobFile = await projectsApiForClient.getProjectPosterPdf({ id: project.id, sourceType: 'other' });
      downloadBlob(blobFile, 'poster.pdf');
    } catch (error) {
      Sentry.captureException(error);
      console.error(error);

      showErrorToast(t('components.PrintPosterDialog.downloadError'));
    }

    setIsLoading(false);
  };

  const handleChangedEmail = useCallback(
    (ev: ChangeEvent<HTMLInputElement>) => {
      setEmail(ev.target.value);
    },
    [setEmail],
  );

  return (
    <>
      <Dialog isOpen={isOpen} onClose={onClose}>
        <h3 className=" text-[32px] md:text-[48px] leading-[32px] md:leading-[48px] mb-[48px] px-[8px] md:px-[16px]">
          {t('components.PrintPosterDialog.title')}
        </h3>
        <div className="h-[258px] w-[178px] overflow-hidden pointer-events-none mx-auto mb-[48px]">
          <iframe
            height={1684}
            width={1190}
            className="scale-[0.15] origin-top-left"
            src={envs.getPosterPreviewUrl(project.numericId)}
          />
        </div>
        <Button
          className="w-full"
          size={ButtonSize.LARGE}
          style={ButtonStyle.PRIMARY}
          tagSpecificProps={{ disabled: isLoading, htmlTag: ButtonHTMLTag.BUTTON, onClick: handleDownload }}
        >
          <span className="flex items-center justify-center">
            <span className="mr-[16px] text-white">
              <DownloadIcon />
            </span>
            <span>{t('components.PrintPosterDialog.downloadText')}</span>
          </span>
        </Button>
        <div className="p-[32px] flex justify-center">{t('components.PrintPosterDialog.orWillBeSent')}</div>
        <div className="h-[50px] mb-[16px]">
          {isEmailSentSuccessfully && !isLoading && (
            <div className="flex gap-[8px] items-center justify-center">
              <CheckGreen className="h-[20px]" />
              <span className="uppercase tracking-[4.5px] text-[12px] text-success-mid">
                {t('components.PrintPosterDialog.emailHasBeenSent')}
              </span>
            </div>
          )}
          {!isEmailSentSuccessfully && (
            <Input
              placeholder={t('components.PrintPosterDialog.emailPlaceholder')}
              isErrorVisible={!!emailErrorMessage}
              errorMessage={emailErrorMessage}
              name={'email'}
              value={email}
              onChange={handleChangedEmail}
            />
          )}
        </div>
        <Button
          className="w-full mb-[32px]"
          style={ButtonStyle.PRIMARY_OUTLINED}
          size={ButtonSize.LARGE}
          tagSpecificProps={{ disabled: isLoading, htmlTag: ButtonHTMLTag.BUTTON, onClick: handleSendPosterByEmail }}
        >
          {isEmailSentSuccessfully
            ? t('components.PrintPosterDialog.sendPosterAgain')
            : t('components.PrintPosterDialog.sendPoster')}
        </Button>
      </Dialog>
    </>
  );
};
